import {
    CandlestickIndicatorInput,
    IndicatorCategory,
    IndicatorMeasure,
    IndicatorPane,
    IndicatorParameterType,
    type IndicatorCandle,
    type IndicatorDefinition,
    type IndicatorProcessInput,
} from '../indicator-definition.js';
import { registerIndicator } from '../indicator-registry.js';
import {
    SequentialIndicatorProcessor,
    type IndicatorCalculationResult,
} from '../sequential-processor.js';
import {
    RollingStandardDeviation,
    type RollingWindowCheckpoint,
} from '../math/index.js';
import {
    LENGTH_STYLE,
    LengthIndicatorParameters,
    close,
    resolvedLength,
} from './shared/core.js';

export class StandardDeviationProcessor extends SequentialIndicatorProcessor<
    IndicatorCandle,
    RollingWindowCheckpoint
> {
    private readonly deviation: RollingStandardDeviation;

    constructor(readonly length: number) {
        super(['line']);
        this.deviation = new RollingStandardDeviation(length);
    }

    protected calculate(
        input: IndicatorProcessInput<IndicatorCandle>,
        commit: boolean,
    ): IndicatorCalculationResult {
        const value = commit
            ? this.deviation.push(close(input))
            : this.deviation.preview(close(input));
        return {
            isFormed: this.deviation.isFormed,
            values: [this.output('line', value, input.index)],
        };
    }

    protected resetState(): void { this.deviation.reset(); }
    protected captureState(): RollingWindowCheckpoint { return this.deviation.checkpoint(); }
    protected restoreState(state: RollingWindowCheckpoint): void {
        this.deviation.restore(state);
    }
}

export const StandardDeviationIndicator: IndicatorDefinition<
    IndicatorCandle,
    LengthIndicatorParameters
> = registerIndicator({
    id: 'StandardDeviation',
    name: 'Standard Deviation',
    description: 'Population standard deviation of the rolling close-price window.',
    category: IndicatorCategory.Volatility,
    input: CandlestickIndicatorInput,
    parameters: [{
        id: 'length',
        name: 'Length',
        description: 'Number of closing prices in the population window.',
        type: IndicatorParameterType.Integer,
        defaultValue: 10,
        min: 1,
        max: 500,
        step: 1,
    }],
    outputs: [{
        id: 'line',
        name: 'StdDev',
        defaultStyle: { ...LENGTH_STYLE, color: '#8d6e63' },
    }],
    naturalPane: IndicatorPane.Separate,
    measure: IndicatorMeasure.MinusOnePlusOne,

    aliases: ['stddev', 'standarddeviation'],
    processorFactory: (parameters) => new StandardDeviationProcessor(
        resolvedLength(parameters, 10, 1),
    ),
});
